import { useMutation, useQuery } from 'react-query';
import { matchExternalRefsAxiosApi } from 'src/Config/axiosApi';

/**
 * Trade360 fixture-binding control plane — `/match-external-refs/*` on
 * `position-opener` (`matchExternalRefsAxiosApi`, base
 * `VITE_POSITION_OPENER_URL`, NOT the `/api/position-opener`-nested
 * `positionOpenerAxiosApi`). Lets an operator bind a group's match to a real
 * Trade360 fixture (`candidates` → `suggest` → `confirm`), flip whether the
 * External Live Signals framework actually acts on it (`acting`), and poll
 * what signals are arriving for a live-tested match (`events`).
 *
 * No list-by-group read exists on the wire — a confirmed binding is only
 * known for the lifetime of the dialog session that confirmed it. See
 * `Trade360FixtureSection`, the only consumer of these hooks.
 */

/** The only provider this framework binds to today. */
export const LSPORTS_TRADE360_PROVIDER = 'lsports-trade360';

export interface MatchExternalRefCandidate {
  fixtureId: string;
  participants: string[];
  league?: string;
}

export interface MatchExternalRefCompetitor {
  providerName: string;
  position: '1' | '2';
  code: string | null;
}

export interface SuggestMatchExternalRefResult {
  matchKey: string;
  provider: string;
  externalId: string;
  resolved: boolean;
  competitors: MatchExternalRefCompetitor[];
}

/** The persisted binding, as returned by `confirm`/`acting`. */
export interface MatchExternalRef {
  _id?: string;
  groupId?: string;
  matchKey: string;
  provider: string;
  externalId: string;
  competitorByPosition?: Record<'1' | '2', string>;
  actingEnabled?: boolean;
  boundBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Mirrors `LiveSignalEvent` on the backend (`live-signal-event.dto.ts`) —
 * field names must match exactly, this is a read-only ops view over the
 * corpus, not a shape either side is free to redefine independently. */
export interface MatchExternalRefEvent {
  signalType: string;
  subject: string | null;
  magnitude: number | null;
  /** Adapter receipt time, ms since epoch — NOT an ISO string. */
  observedAtMs: number;
}

/** `POST /match-external-refs/candidates` — search Trade360 fixtures for a match. */
export function useMatchExternalRefCandidates() {
  return useMutation(
    async (vars: { groupId: string; matchKey: string }) => {
      const { data } = await matchExternalRefsAxiosApi.post<
        { candidates: MatchExternalRefCandidate[] } | MatchExternalRefCandidate[]
      >('/match-external-refs/candidates', vars);
      return Array.isArray(data) ? data : (data?.candidates ?? []);
    },
    { mutationKey: 'match-external-refs:candidates' }
  );
}

/** `POST /match-external-refs/suggest` — preview roster resolution for a candidate. */
export function useSuggestMatchExternalRef() {
  return useMutation(
    async (vars: {
      groupId: string;
      matchKey: string;
      provider: string;
      externalId: string;
    }) => {
      const { data } =
        await matchExternalRefsAxiosApi.post<SuggestMatchExternalRefResult>(
          '/match-external-refs/suggest',
          vars
        );
      return data;
    },
    { mutationKey: 'match-external-refs:suggest' }
  );
}

/** `POST /match-external-refs/confirm` — persist the fixture binding. */
export function useConfirmMatchExternalRef() {
  return useMutation(
    async (vars: {
      groupId: string;
      matchKey: string;
      provider: string;
      externalId: string;
      competitorByPosition: Record<'1' | '2', string>;
      boundBy: string;
    }) => {
      const { data } = await matchExternalRefsAxiosApi.post<MatchExternalRef>(
        '/match-external-refs/confirm',
        vars
      );
      return data;
    },
    { mutationKey: 'match-external-refs:confirm' }
  );
}

/** `POST /match-external-refs/acting` — flip whether the framework acts on this fixture. */
export function useSetMatchExternalRefActing() {
  return useMutation(
    async (vars: {
      provider: string;
      externalId: string;
      actingEnabled: boolean;
      updatedBy: string;
    }) => {
      const { data } = await matchExternalRefsAxiosApi.post<MatchExternalRef>(
        '/match-external-refs/acting',
        vars
      );
      return data;
    },
    { mutationKey: 'match-external-refs:acting' }
  );
}

export const MATCH_EXTERNAL_REF_EVENTS_KEY = 'match-external-ref-events';

/**
 * `GET /match-external-refs/events?matchKey=&limit=` — polled ONLY while the
 * caller says so (`enabled`): the events panel starts collapsed, and this
 * must not poll a match nobody is looking at. Sorted reverse-chronological
 * here rather than trusting the wire order.
 */
export function useMatchExternalRefEvents(
  matchKey: string | null,
  opts?: { enabled?: boolean; limit?: number }
) {
  const limit = opts?.limit ?? 200;
  return useQuery<MatchExternalRefEvent[]>(
    [MATCH_EXTERNAL_REF_EVENTS_KEY, matchKey, limit],
    async () => {
      const { data } = await matchExternalRefsAxiosApi.get<
        MatchExternalRefEvent[] | { events: MatchExternalRefEvent[] }
      >('/match-external-refs/events', { params: { matchKey, limit } });
      const events = Array.isArray(data) ? data : (data?.events ?? []);
      return [...events].sort((a, b) => b.observedAtMs - a.observedAtMs);
    },
    {
      enabled: (opts?.enabled ?? true) && !!matchKey,
      refetchInterval: 5_000,
      keepPreviousData: true
    }
  );
}
